/**
 * Money handed over in person — a check or cash — and the receipt it earns.
 *
 * Jonathon, 2026-09-10, after Sherley Gillespie paid a $450 repair by check
 * and her receipt had to be made by hand: "we need to build a process where
 * this happens automatically." Mercury receipts every card and bank payment
 * itself; cash and checks never pass through it, so nobody was acknowledging
 * them at all.
 *
 * His rules, the same day, which this file is the only statement of:
 *
 *  - **Cash is paid** the moment it is recorded.
 *  - **A check is submitted** until it is in the bank, and **paid** once it is.
 *  - **The receipt goes on whichever comes first**: a rep recording the check,
 *    or the check deposit appearing in Mercury. One receipt, not two.
 *  - Nobody is copied; the PDF goes on the job's Docs card.
 *
 * Pure: no DOM, no network. The browser records, draws and sends; this decides
 * what the payment is, what it ticks, whose it is and what the receipt says.
 * See ADR 0125.
 */

import { jobBalance, type Balance, type BalanceInput } from "./balance.js";
import { COMPANY_NAME, PAYMENT_SLOT_LABEL } from "./constants.js";
import { companyAddressLines, invoiceAddressLines } from "./invoice.js";
import { type Cents, cents, formatMoney } from "./money.js";
import {
  paymentInitiated,
  recordedPaymentThisCreditIs,
} from "./moneyInTheBank.js";
import { mayTickClearedBox, PLAN_FINAL_CHECK_REFUSED } from "./paymentPlan.js";
import {
  labelsFor,
  PAYMENT_CHECKLIST_LABELS,
  type PaymentMatch,
  type PaymentSlot,
} from "./paymentReceipt.js";

/**
 * How a payment entered by hand was made. Checks and cash since ADR 0125; a
 * card taken outside Roof Board, a bank transfer (Zelle, a wire) and a
 * financing company's payout since 2026-10-14 (ADR 0288).
 */
export type CustomerPaymentMethod =
  "cash" | "check" | "card" | "transfer" | "financing";
export type CustomerPaymentState = "submitted" | "paid";

/** Every method, in the order the form offers them. */
export const CUSTOMER_PAYMENT_METHODS: readonly CustomerPaymentMethod[] = [
  "check",
  "cash",
  "card",
  "transfer",
  "financing",
];

export const CUSTOMER_PAYMENT_METHOD_LABEL: Record<
  CustomerPaymentMethod,
  string
> = {
  cash: "Cash",
  check: "Check",
  card: "Card",
  transfer: "Bank transfer",
  financing: "Financing",
};

/**
 * The methods a person settles by saying the money is in the bank: a check,
 * a card, a transfer or a financing payout, by ticking First or Final Check
 * or by "It's in the bank" on the Payments tab. Cash is paid the moment it
 * is recorded and is never among them (ADR 0125, ADR 0159, ADR 0288,
 * ADR 0312).
 */
export function settledByAPerson(method: string): boolean {
  return (
    method === "check" ||
    method === "card" ||
    method === "transfer" ||
    method === "financing"
  );
}

/** The methods "It's in the bank" settles, for a write to guard on. */
export const MARKED_IN_THE_BANK_METHODS: readonly CustomerPaymentMethod[] = [
  "check",
  "card",
  "transfer",
  "financing",
];

/**
 * Whether "It's in the bank" may settle this payment: a check, card,
 * transfer or financing payout entered by hand and still waiting. Never
 * cash, which was paid on recording (ADR 0288).
 *
 * A check was left out until ADR 0312: it was settled by its deposit or by
 * ticking its box, and a check for a middle draw or a payment plan has no
 * box. Jonathon, 2026-10-26: say a payment is already in the bank and have
 * it count at once, "instead of waiting".
 */
export function mayMarkInTheBank(row: {
  method: string;
  state: string;
}): boolean {
  return (
    row.state === "submitted" &&
    (MARKED_IN_THE_BANK_METHODS as readonly string[]).includes(row.method)
  );
}

/**
 * Whether Roof Board may send this payment a receipt of its own.
 *
 * Not for a payment link's money a person said was in the bank: the customer
 * paid through Mercury, and Mercury emailed them the receipt (ADR 0312). A
 * second one from us, numbered differently, is two receipts for one payment.
 * Every other payment recorded here gets ours (ADR 0125, ADR 0140).
 */
export function mayReceipt(payment: {
  mercury_payment_request_id?: string | null;
}): boolean {
  return !payment.mercury_payment_request_id;
}

/** What the Balance card says in place of a receipt Mercury sent. */
export const RECEIPTED_BY_MERCURY = "Receipted by Mercury";

/** Why the receipt prompt refuses a link payment. */
export const RECEIPT_REFUSED_FOR_LINK =
  "The customer paid this through a Mercury payment link, and Mercury " +
  "emailed them the receipt. Roof Board does not send a second one.";

/**
 * The credit already confirmed on the job that a payment, just marked as in
 * the bank, is.
 *
 * The office types in "Zelle, $2,500" and somebody confirms the $2,500 credit
 * on Money In, in either order. Confirmed after, `recordedPaymentThisCreditIs`
 * ties the credit to the paid payment, as it does banked cash. Confirmed
 * before, the payment was still on its way and nothing tied, so this is that
 * same rule run from the payment's side the moment It's in the bank is
 * pressed. Without it the balance counts the payment and the untied credit
 * both (ADR 0288).
 *
 * Only a credit nothing else holds — no recorded payment and no payment link
 * points at it — and never a check deposit, which is a check's. Oldest first,
 * so two transfers of one amount take two credits.
 *
 * And never a credit confirmed for another payment: on a 50/50 schedule the
 * First and the Final are one amount, and a Final transfer must not take the
 * First's credit (review of #937). A credit with no slots is anybody's; one
 * with slots is only for a payment that shares one.
 *
 * A link's money said to be in the bank (ADR 0312) is tied the same way,
 * when its press comes after a credit the bank showed without naming the
 * link. A credit the link itself holds is held elsewhere and never taken.
 */
export function creditThisPaymentIs(
  payment: {
    id: string;
    /** A check is tied only by its deposit, never by amount (ADR 0159). */
    method?: string;
    state: string;
    amountCents: number | null;
    slots?: readonly number[] | null;
    towardPlan?: boolean;
    depositId?: string | null;
    /** The link this records the money for, said in the bank (ADR 0312). */
    mercuryRequestId?: string | null;
  },
  credits: readonly {
    id: string;
    amountCents: number;
    postedAt?: string | null;
    description?: string | null;
    /** A recorded payment or a payment link already points at this credit. */
    heldElsewhere?: boolean;
    /** The scheduled payments it was confirmed for, if any. */
    slots?: readonly number[] | null;
  }[],
): string | null {
  if (payment.depositId) return payment.depositId;
  // A check marked in the bank is tied to its check deposit when Mercury
  // shows it, by the check's own matching; a Zelle of the same amount is
  // somebody else's money.
  if (payment.method === "check") return null;
  const free = credits
    .filter((credit) => !credit.heldElsewhere)
    .filter(
      (credit) => !isCheckDeposit({ description: credit.description ?? null }),
    )
    .slice()
    .sort((a, b) => (a.postedAt ?? "").localeCompare(b.postedAt ?? ""));
  return (
    free.find(
      (credit) => recordedPaymentThisCreditIs([payment], credit) === payment.id,
    )?.id ?? null
  );
}

/** How a customer paid a link, as the payment it is recorded as. */
const LINK_METHOD_RECORDED: Record<string, CustomerPaymentMethod> = {
  card: "card",
  ach: "transfer",
  financing: "financing",
};

/**
 * The payment a person records by saying a link's money is already in the
 * bank (ADR 0312).
 *
 * Jonathon, 2026-10-26: "say a payment is already in the bank and it updates
 * that automatically instead of waiting." A paid card link sits at Stripe for
 * two or three days and the balance waits for the credit (ADR 0139). When
 * the office knows the money has landed, this is the record that counts it:
 * the link's own amount, which is what it billed before any card surcharge,
 * against the link's own payment, paid as of today. The customer was already
 * receipted by Mercury, so no receipt goes.
 *
 * Only a link the customer has started paying, and only one nobody has
 * already recorded.
 */
export function recordForLink(
  link: {
    id: string;
    state: string;
    slot: number | null;
    amountCents: number | null;
    paymentStartedAt: string | null;
    paidAt?: string | null;
    customerMethod: string | null;
  },
  recordedForLinks: readonly { mercuryRequestId?: string | null }[] = [],
):
  | {
      allowed: true;
      method: CustomerPaymentMethod;
      amountCents: number;
      slots: number[];
    }
  | { allowed: false; why: string } {
  if (recordedForLinks.some((p) => p.mercuryRequestId === link.id)) {
    return { allowed: false, why: "This payment is already recorded." };
  }
  if (
    !paymentInitiated({
      state: link.state,
      amountCents: link.amountCents,
      paymentStartedAt: link.paymentStartedAt ?? link.paidAt ?? null,
    })
  ) {
    return {
      allowed: false,
      why: "The customer has not paid this link yet, so there is nothing to put in the bank.",
    };
  }
  const amountCents = Math.round(Number(link.amountCents ?? 0));
  if (!(amountCents > 0)) {
    return { allowed: false, why: "This link has no amount on it." };
  }
  const slot = link.slot == null ? null : Number(link.slot);
  return {
    allowed: true,
    method: LINK_METHOD_RECORDED[link.customerMethod ?? ""] ?? "transfer",
    amountCents,
    slots: slot != null && Number.isFinite(slot) ? [slot] : [],
  };
}

/** "Check no. 2047", or the method's own name. What a receipt and the Payments tab print. */
export function paymentMethodText(
  method: CustomerPaymentMethod,
  checkNumber?: string | null,
): string {
  const number = (checkNumber ?? "").trim();
  if (method === "check" && number) return `Check no. ${number}`;
  return CUSTOMER_PAYMENT_METHOD_LABEL[method] ?? method;
}

/**
 * Where a payment starts.
 *
 * Cash is money in hand, so it is paid. A check is a promise the bank has not
 * kept yet — it can bounce — so it is submitted until Mercury shows it
 * deposited. The database holds the same line (`customer_payments_cash_is_paid`).
 */
export function stateOnRecording(
  method: CustomerPaymentMethod,
  clearedByTickedBox = false,
): CustomerPaymentState {
  return method === "cash" || clearedByTickedBox ? "paid" : "submitted";
}

/**
 * A payment recorded from a still-ticked Check box is already in the bank.
 *
 * Any method a person settles by ticking the box (`settledByAPerson`): a card,
 * transfer or financing payout recorded there is in the bank as a check is
 * (ADR 0288). Recorded as owed, no settle would ever run for it, because the
 * box is already ticked.
 */
export function recordedFromClearedOffer(input: {
  method: CustomerPaymentMethod;
  offeredSlot: PaymentSlot | null | undefined;
  recordedSlots: readonly number[];
  clearedBoxStatus: string | null | undefined;
}): boolean {
  return (
    settledByAPerson(input.method) &&
    input.offeredSlot != null &&
    input.recordedSlots.includes(input.offeredSlot) &&
    input.clearedBoxStatus === "done"
  );
}

/**
 * The checklist boxes recording this payment ticks.
 *
 * The pair on the production checklist is "First Check Submitted" and "First
 * Check" (and the same for Final): submitted is the payment being taken, the
 * plain one is the money being in the bank. Cash is both at once. A check is
 * only the first until it clears.
 */
export function labelsOnRecording(
  method: CustomerPaymentMethod,
  match: PaymentMatch,
): string[] {
  const submitted = labelsFor(match, "submitted");
  return method === "cash"
    ? [...submitted, ...labelsFor(match, "cleared")]
    : submitted;
}

/**
 * The payment a Submitted box is about, or null for any other box.
 *
 * Ticking "First Check Submitted" by hand is a rep saying they have taken the
 * first payment — Jonathon's "when a rep clicks on payment submitted" — so the
 * job screen asks how, and receipts it.
 */
export function slotForSubmittedBox(label: string): PaymentSlot | null {
  if (label === PAYMENT_CHECKLIST_LABELS[1]?.submitted) return 1;
  if (label === PAYMENT_CHECKLIST_LABELS[4]?.submitted) return 4;
  return null;
}

/**
 * The payment a cleared box is about, or null for any other box.
 *
 * The other half of `slotForSubmittedBox`. "First Check" and "Final Check" —
 * the plain ones, without "Submitted" — are a person saying the money is in
 * the bank.
 */
export function slotForClearedBox(label: string): PaymentSlot | null {
  if (label === PAYMENT_CHECKLIST_LABELS[1]?.cleared) return 1;
  if (label === PAYMENT_CHECKLIST_LABELS[4]?.cleared) return 4;
  return null;
}

/**
 * The recorded checks that a cleared box being ticked settles.
 *
 * Tu Lee's job 1032, 2026-09-14: his $1,100 check was recorded on 9/11 and
 * never appeared in the Mercury account, so it stayed `submitted`. Jonathon
 * ticked "Final Check" to say it had cleared, and the balance went on reading
 * $1,100 owed — because the balance counts payments, not boxes, and nothing
 * anywhere let a person say a check had landed. His words: "tu lee still shows
 * money owed even though I said he made his payment in full."
 *
 * Only a check, and only one still waiting: cash is paid the moment it is
 * recorded, and a check already `paid` needs nothing. Only the slot the box is
 * about, so ticking Final Check cannot settle a first payment nobody has seen.
 * A card, transfer or financing payout entered by hand is settled the same
 * way, because the box makes the same claim about it (`settledByAPerson`,
 * ADR 0288).
 *
 * `checksUnsettledByBox` runs it backwards when the box is unticked, which is
 * the one way a paid payment becomes owed again (ADR 0159).
 */
export function checksSettledByBox(
  slot: PaymentSlot,
  rows: readonly {
    id: string;
    method: string;
    state: string;
    slots: readonly number[] | null;
  }[],
): string[] {
  return rows
    .filter((row) => settledByAPerson(row.method) && row.state === "submitted")
    .filter((row) => (row.slots ?? []).map(Number).includes(Number(slot)))
    .map((row) => row.id);
}

/**
 * Whether the bank has shown a recorded payment's money: it carries the
 * transaction or the credit it settled, or it is a link's money said to be
 * in the bank and the link's own credit is confirmed (ADR 0312). That credit
 * is tied to the link, not to the record, and is the same money.
 */
function heldByTheBank(
  row: {
    mercury_transaction_id?: string | null;
    mercury_deposit_id?: string | null;
    mercury_payment_request_id?: string | null;
  },
  landedLinks: ReadonlySet<string>,
): boolean {
  return (
    !!row.mercury_transaction_id ||
    !!row.mercury_deposit_id ||
    (!!row.mercury_payment_request_id &&
      landedLinks.has(row.mercury_payment_request_id))
  );
}

/**
 * The checks that a cleared box being UNticked puts back to waiting.
 *
 * Jonathon, 2026-09-14, asked what happens when somebody ticks Final Check by
 * mistake: "check goes back to unpaid". Ticking the box is a person's word
 * that the money landed, and a person's word is the kind of thing that can be
 * taken back — so unticking it owes the same balance it did a minute ago.
 *
 * Only what the box itself banked. A check Mercury saw deposited carries the
 * transaction it settled (`mercury_transaction_id`) or the deposit row
 * (`mercury_deposit_id`), whether Roof Board matched it by itself or somebody
 * matched it on Money In; the bank saying so does not stop being true because
 * a box was unticked, and that money stays where it is. What is left — a check
 * `paid` with neither — became paid here and nowhere else.
 *
 * Cash is never touched. It is paid from the moment it is recorded (ADR 0125)
 * and no box made it so. A card, transfer or financing payout settled by a
 * person, by this box or by "It's in the bank", goes back like a check: that
 * was a person's word too (ADR 0288). A link's money said to be in the bank
 * goes back too, unless the link's own credit has landed by then
 * (`landedLinks`, ADR 0312).
 *
 * The receipt is not withdrawn either. It went when the check was recorded and
 * it was true then; the customer handed over a check, and that is all it ever
 * claimed.
 */
export function checksUnsettledByBox(
  slot: PaymentSlot,
  rows: readonly {
    id: string;
    method: string;
    state: string;
    slots: readonly number[] | null;
    mercury_transaction_id?: string | null;
    mercury_deposit_id?: string | null;
    mercury_payment_request_id?: string | null;
  }[],
  landedLinks: ReadonlySet<string> = new Set(),
): string[] {
  return rows
    .filter((row) => settledByAPerson(row.method) && row.state === "paid")
    .filter((row) => !heldByTheBank(row, landedLinks))
    .filter((row) => (row.slots ?? []).map(Number).includes(Number(slot)))
    .map((row) => row.id);
}

/**
 * The recorded checks an untick leaves paid, because the bank has seen them.
 *
 * The other side of `checksUnsettledByBox`: a paid check on this payment that
 * carries a Mercury transaction or deposit. Unticking the box does not put
 * these back — the untick is refused for them, and the screen says so rather
 * than leaving somebody to wonder why the balance did not move (ADR 0159).
 */
export function checksTheBankHolds(
  slot: PaymentSlot,
  rows: readonly {
    id: string;
    method: string;
    state: string;
    slots: readonly number[] | null;
    mercury_transaction_id?: string | null;
    mercury_deposit_id?: string | null;
    mercury_payment_request_id?: string | null;
  }[],
  landedLinks: ReadonlySet<string> = new Set(),
): string[] {
  return rows
    .filter((row) => settledByAPerson(row.method) && row.state === "paid")
    .filter((row) => heldByTheBank(row, landedLinks))
    .filter((row) => (row.slots ?? []).map(Number).includes(Number(slot)))
    .map((row) => row.id);
}

/**
 * What a box changing status means for the recorded checks on its payment.
 *
 * "First Check" or "Final Check" going to done is a person saying the money
 * is in the bank, so the check waiting on that payment settles. The same box
 * leaving done takes that back. Every other box, and every change that is not
 * into or out of done, means nothing to the money (ADR 0159).
 *
 * The rule lives here rather than in the job screen so that every path which
 * ticks a box asks the same question (CLAUDE.md rule 5).
 */
export function clearedBoxChange(
  label: string,
  before: string,
  after: string,
): { direction: "settle" | "unsettle"; slot: PaymentSlot } | null {
  const slot = slotForClearedBox(label);
  if (!slot) return null;
  if (after === "done" && before !== "done") {
    return { direction: "settle", slot };
  }
  if (before === "done" && after !== "done") {
    return { direction: "unsettle", slot };
  }
  return null;
}

/**
 * What to say when First or Final Check is ticked with nothing to settle.
 *
 * Jonathon, 2026-09-21, asked what ticking the box should do when no check was
 * ever recorded — the job moved to complete, no money moved, the balance went
 * on reading owed and the app said nothing: "Say so, and offer to record the
 * payment right there" (ADR 0182).
 *
 * One sentence for every path that ticks the box, so the job screen and an
 * agent's `set_checklist_item` say the same thing about the same job. Each
 * adds what happened to the box after it: a person's tick still stands (their
 * word, ADR 0159); an agent's is refused, because an agent needs a bank credit.
 */
export function nothingToSettle(slot: PaymentSlot): string {
  const payment = (PAYMENT_SLOT_LABEL[slot] ?? `payment ${slot}`).toLowerCase();
  const box = PAYMENT_CHECKLIST_LABELS[slot]?.cleared ?? "the box";
  return (
    `Nothing recorded on this job says the ${payment} is in the bank: no ` +
    `check is waiting on it, no cash was taken for it, and no deposit is ` +
    `confirmed for it on Money In, so ticking ${box} settles nothing.`
  );
}

/**
 * The payment a person's tick of First or Final Check settled nothing on,
 * while the balance still counts it as owed — or null when there is nothing
 * to say.
 *
 * Null for any other box, for an untick, for a tick that settled a check, and
 * for a payment the balance already counts as paid: most ticks land on a job
 * whose deposit did the work already, and those need no word at all. Null too
 * when there is no balance to read, or no such payment on the schedule — a job
 * with no price owes nothing to say anything about.
 *
 * `settled` is how many recorded checks the tick settled; `balance` is the
 * job's `jobBalance`. A tick that settled nothing moved no money, so the
 * balance from before the tick is the balance after it.
 */
export function tickSettledNothing(input: {
  label: string;
  before: string;
  after: string;
  settled: number;
  balance: Pick<Balance, "lines"> | null;
}): PaymentSlot | null {
  const change = clearedBoxChange(input.label, input.before, input.after);
  if (!change || change.direction !== "settle") return null;
  if (input.settled > 0) return null;
  const line = input.balance?.lines.find((row) => row.slot === change.slot);
  if (!line || line.paid) return null;
  return change.slot;
}

/** A recorded payment, as far as `agentMaySettle` needs to see it. */
export interface PaymentForCredit {
  id: string;
  job_id: string;
  method: string;
  state: string;
  amount_cents: number;
  slots: readonly number[] | null;
  mercury_transaction_id?: string | null;
  mercury_deposit_id?: string | null;
}

/** A Mercury credit, as far as `agentMaySettle` needs to see it. */
export interface BankCredit {
  id: string;
  job_id: string | null;
  state: string;
  amount_cents: number;
  slots: readonly number[] | null;
  mercury_transaction_id: string;
  posted_at: string | null;
}

/** A check an agent's tick settles, and the credit that proves it landed. */
export interface CheckSettledByCredit {
  paymentId: string;
  depositId: string;
  mercuryTransactionId: string;
  paidAt: string | null;
}

export type AgentSettleVerdict =
  | { allowed: true; settle: CheckSettledByCredit[] }
  | { allowed: false; why: string };

/**
 * The Mercury credit that proves a recorded check landed, or null.
 *
 * The tie is the one Money In's confirm makes (`checkConfirmedByAPerson`):
 * the check carries the deposit's id, or the transaction the deposit is. And
 * the deposit must be confirmed against this same job, for at least the
 * check's amount — a credit somebody put on another customer's job proves
 * nothing about this one.
 */
function creditFor(
  jobId: string,
  slot: PaymentSlot,
  payment: PaymentForCredit,
  credits: readonly BankCredit[],
): BankCredit | null {
  return (
    credits.find(
      (credit) =>
        credit.state === "confirmed" &&
        credit.job_id === jobId &&
        credit.amount_cents >= payment.amount_cents &&
        (credit.slots ?? []).map(Number).includes(Number(slot)) &&
        ((!!payment.mercury_deposit_id &&
          payment.mercury_deposit_id === credit.id) ||
          (!!payment.mercury_transaction_id &&
            payment.mercury_transaction_id === credit.mercury_transaction_id)),
    ) ?? null
  );
}

/**
 * Whether an AI agent may tick "First Check" or "Final Check", and which
 * recorded checks that tick settles.
 *
 * Jonathon, 2026-09-19: "Agents can mark it paid, but only if they've actually
 * confirmed the money is linked to that customer and has hit our bank account.
 * If the agent can't see the matching credit, it should refuse rather than
 * tick the box." A person's tick is their word (ADR 0159); an agent's is not
 * enough on its own, so it needs the bank's.
 *
 * - Every check recorded on the payment must have its own credit, confirmed
 *   against this job in Mercury (`creditFor`). One without is refused, and
 *   the box stays as it was.
 * - With no check recorded on the payment, a credit confirmed against this
 *   job for that payment (Money In's `slots`) is enough — a card or bank
 *   payment Money In has already matched.
 * - With neither, there is nothing in the bank to point to, and it refuses.
 *   Cash is money in hand, not in the bank, so it proves nothing here.
 *
 * The checks it settles are the ones still `submitted`, each with the credit
 * that proves it, so the write ties them the way Money In does and an untick
 * later leaves them alone.
 */
export function agentMaySettle(input: {
  jobId: string;
  slot: PaymentSlot;
  payments: readonly PaymentForCredit[];
  credits: readonly BankCredit[];
  /** Whether the job is on an in-house payment plan (ADR 0286). */
  onPlan?: boolean;
  /** Whether that plan's banked payments have paid it off. */
  planPaidOff?: boolean;
}): AgentSettleVerdict {
  const { jobId, slot, credits } = input;
  // On a plan the Final is paid month by month: Final Check waits for the
  // plan to be paid off, the same rule the web app asks.
  if (
    !mayTickClearedBox(slot, {
      onPlan: input.onPlan === true,
      paidOff: input.planPaidOff === true,
    })
  ) {
    return { allowed: false, why: PLAN_FINAL_CHECK_REFUSED };
  }
  const label = PAYMENT_SLOT_LABEL[slot] ?? `payment ${slot}`;
  const onSlot = (slots: readonly number[] | null) =>
    (slots ?? []).map(Number).includes(Number(slot));

  const checks = input.payments.filter(
    (row) =>
      row.job_id === jobId && row.method === "check" && onSlot(row.slots),
  );

  const settle: CheckSettledByCredit[] = [];
  for (const check of checks) {
    const credit = creditFor(jobId, slot, check, credits);
    if (!credit) {
      return {
        allowed: false,
        why:
          `The ${formatMoney(cents(check.amount_cents))} check recorded for the ` +
          `${label} has no deposit in Mercury confirmed against this job, so ` +
          `there is no bank credit to show it landed. Confirm the deposit on ` +
          `Money In first; the box was not ticked.`,
      };
    }
    if (check.state === "submitted") {
      settle.push({
        paymentId: check.id,
        depositId: credit.id,
        mercuryTransactionId:
          check.mercury_transaction_id ?? credit.mercury_transaction_id,
        paidAt: credit.posted_at,
      });
    }
  }

  if (checks.length === 0) {
    const credited = credits.some(
      (credit) =>
        credit.state === "confirmed" &&
        credit.job_id === jobId &&
        onSlot(credit.slots),
    );
    // Nothing recorded at all — no check, no cash — is the case ADR 0182 is
    // about, and an agent says it in the same words the job screen does.
    const cashTaken = input.payments.some(
      (row) =>
        row.job_id === jobId && row.state === "paid" && onSlot(row.slots),
    );
    if (!credited && !cashTaken) {
      return {
        allowed: false,
        why:
          `${nothingToSettle(slot)} There is no bank credit to show the ` +
          `money landed, so the box was not ticked. Somebody can record the ` +
          `payment on the job, or confirm its deposit on Money In, first.`,
      };
    }
    if (!credited) {
      return {
        allowed: false,
        why:
          `No deposit in Mercury is confirmed against this job for the ` +
          `${label}, so there is no bank credit to show the money landed. ` +
          `Confirm it on Money In first; the box was not ticked.`,
      };
    }
  }

  return { allowed: true, settle };
}

/** The boxes a check ticks when it clears, for the slots it was recorded as. */
export function labelsOnClearing(slots: readonly PaymentSlot[]): string[] {
  return labelsFor(
    { matched: true, slots: [...slots], why: "" } as PaymentMatch,
    "cleared",
  );
}

/**
 * The slots whose money counts as in, from recorded payments.
 *
 * The third source for `jobBalance`, beside Mercury's paid invoices and
 * confirmed deposits. Only `paid` rows — cash, and checks that have cleared.
 * A submitted check is not money in the bank, and the balance is about money
 * in the bank (ADR 0116).
 */
export function paidSlotsFromCustomerPayments(
  rows: readonly { state: string; slots: readonly number[] | null }[],
): number[] {
  return rows
    .filter((row) => row.state === "paid")
    .flatMap((row) => (row.slots ?? []).map(Number));
}

// ------------------------------------------------- a check seen in Mercury ---

/**
 * Whether a credit on the Mercury account is a check somebody deposited.
 *
 * Mercury writes these as "Check Deposit From Michael Lewis" (job 1033,
 * 2026-09-04), and marks the transaction's kind as a check deposit. Either
 * will do.
 */
export function isCheckDeposit(input: {
  description?: string | null;
  kind?: string | null;
}): boolean {
  const kind = (input.kind ?? "").toLowerCase().replace(/[^a-z]/g, "");
  if (kind === "checkdeposit") return true;
  return /\bcheck deposit\b/i.test(input.description ?? "");
}

/** Who wrote the check, from what Mercury says about the deposit. */
export function checkPayerName(input: {
  counterparty?: string | null;
  description?: string | null;
}): string | null {
  const named = (input.counterparty ?? "").trim();
  if (named) return named;
  const found = /check deposit from\s+(.+)$/i.exec(
    (input.description ?? "").trim(),
  );
  return found?.[1]?.trim() || null;
}

/** A name reduced to its words, in any order and any case. */
function nameWords(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .sort();
}

/**
 * Whether two names are the same person's, as far as a bank line can say.
 *
 * Same words, any order, any case: "GILLESPIE SHERLEY" is Sherley Gillespie.
 * A middle initial on one and not the other is NOT the same — this decides who
 * gets emailed a receipt, and a near miss is somebody else's customer.
 */
export function sameName(a: string, b: string): boolean {
  const left = nameWords(a);
  const right = nameWords(b);
  return (
    left.length > 0 &&
    left.length === right.length &&
    left.every((word, at) => word === right[at])
  );
}

/** A job a check might belong to. */
export interface CheckCandidate {
  jobId: string;
  customerName: string;
  /** What is still owed on the job, from `jobBalance`. */
  outstanding: Cents;
  /** The scheduled payments not yet in, from `jobBalance`'s lines. */
  due: readonly Cents[];
}

export type CheckIdentity =
  { jobId: string; why: string } | { jobId: null; why: string };

/**
 * Whose check this is, when that can be said without asking anybody.
 *
 * A receipt goes to a customer by email the moment this answers, so it only
 * answers when there is no doubt at all:
 *
 *  - the name on the deposit is a customer's name, word for word;
 *  - exactly one job of theirs — of anybody's by that name — is owed money;
 *  - and the check is exactly a payment that job is waiting for, or exactly
 *    what is left on it.
 *
 * Anything else is a person's call. The deposit is not lost: once it posts it
 * is on the Money In list like any other, and confirming it there sends the
 * receipt.
 */
export function identifyCheck(input: {
  payer: string | null;
  amountCents: Cents;
  candidates: readonly CheckCandidate[];
}): CheckIdentity {
  const payer = (input.payer ?? "").trim();
  const amount = cents(input.amountCents);
  if (!payer) {
    return {
      jobId: null,
      why: "The deposit does not say who wrote the check.",
    };
  }

  const named = input.candidates.filter((candidate) =>
    sameName(candidate.customerName, payer),
  );
  if (named.length === 0) {
    return { jobId: null, why: `No customer is called ${payer}.` };
  }

  const owed = named.filter((candidate) => candidate.outstanding > 0);
  const fits = owed.filter(
    (candidate) =>
      candidate.outstanding === amount || candidate.due.includes(amount),
  );

  if (fits.length === 1) {
    return {
      jobId: fits[0]!.jobId,
      why: `${formatMoney(amount)} from ${payer} is a payment their job is waiting for.`,
    };
  }
  if (fits.length > 1) {
    return {
      jobId: null,
      why: `More than one job for ${payer} is waiting for ${formatMoney(amount)}.`,
    };
  }
  return {
    jobId: null,
    why: `${payer} has no payment of ${formatMoney(amount)} due.`,
  };
}

// ------------------------------------------------------------ the receipt ---

export interface ReceiptSnapshot {
  /** 1034-1: the job, and which payment on it. Numbered by the database. */
  number: string;
  /** The day the money was received, as an ISO date. */
  receivedOn: string;
  company: {
    name: string;
    /** One line, as typed in Tools. */
    address: string | null;
    phone: string | null;
    website: string | null;
    addressLines: string[];
  };
  customer: { name: string; addressLines: string[] };
  jobNumber: string;
  /** The service address — which property this paid for. */
  property: string | null;
  /** What it paid: "Final Payment", or "Payment" when it was none of them. */
  description: string;
  /** "Check no. 2047", "Check", "Cash". */
  method: string;
  amount: Cents;
  /** The contract. Null when the job has no price, which prints no totals. */
  contract: Cents | null;
  /**
   * What is left after this payment, from `jobBalance`. Null when the payment
   * settles no scheduled payment, because then no honest figure exists: the
   * balance card counts payments, not loose amounts, and a receipt that
   * disagreed with it is the fault ADR 0116 exists to prevent.
   */
  balanceAfter: Cents | null;
  paidInFull: boolean;
  /** Who to ask. The rep on the job, in full (never a first name alone). */
  contact: { name: string; phone: string | null; email: string | null } | null;
}

export interface BuildReceiptInput {
  number: string;
  receivedOn: string;
  company: {
    name?: string | null | undefined;
    address?: string | null | undefined;
    phone?: string | null | undefined;
    website?: string | null | undefined;
  };
  customer: {
    full_name?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  };
  jobNumber: string | number;
  jobAddress: {
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  };
  method: CustomerPaymentMethod;
  checkNumber?: string | null;
  amount: Cents;
  slots: readonly PaymentSlot[];
  /**
   * A monthly payment toward an in-house payment plan (ADR 0286). It names no
   * slot, and `balance` must already count it — read after it was recorded —
   * as paid or on its way.
   */
  towardPlan?: boolean;
  /**
   * The job's balance as it stood, for working out what is left. This payment
   * is added to it here, so a check that has not cleared still reads to the
   * customer as the payment it is.
   */
  balance: BalanceInput | null;
  rep: {
    full_name?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
}

/** A street address without the country the invoice adds for carriers. */
function homeLines(input: BuildReceiptInput["customer"]): string[] {
  return invoiceAddressLines(input).filter((line) => line !== "United States");
}

export function buildReceipt(input: BuildReceiptInput): ReceiptSnapshot {
  const amount = cents(input.amount);
  const slots = [...input.slots];

  let contract: Cents | null = null;
  let balanceAfter: Cents | null = null;
  let wholeJob = false;
  if (input.balance && input.balance.contract > 0) {
    contract = cents(input.balance.contract);
    if (slots.length > 0) {
      const before = jobBalance(input.balance).outstanding;
      balanceAfter = jobBalance({
        ...input.balance,
        paidSlots: [...input.balance.paidSlots, ...slots],
      }).outstanding;
      // Nothing had been paid and now nothing is owed: one payment for the
      // lot. "Final Payment" is what the schedule calls the only line on a
      // small job, and it reads oddly on the only payment a customer made.
      wholeJob = before === contract && balanceAfter === 0;
    } else if (input.towardPlan) {
      // What is left once this check, and any other already sent, arrives.
      // It is in `balance` already, as paid or on its way, so it is not
      // added a second time here.
      balanceAfter = jobBalance(input.balance).toCollect;
    }
  }

  const described = slots
    .map((slot) => PAYMENT_SLOT_LABEL[slot] ?? `Payment ${slot}`)
    .join(" and ");
  const method = paymentMethodText(input.method, input.checkNumber);

  const street = homeLines(input.jobAddress);
  const repName = (input.rep?.full_name ?? "").trim();

  return {
    number: input.number,
    receivedOn: input.receivedOn,
    company: {
      name: (input.company.name ?? "").trim() || COMPANY_NAME,
      address: (input.company.address ?? "").trim() || null,
      phone: (input.company.phone ?? "").trim() || null,
      website: (input.company.website ?? "").trim() || null,
      // One field typed in Tools, split where the street ends — the same
      // reading the invoice gives it, less the country.
      addressLines: companyAddressLines(input.company.address).filter(
        (line) => line !== "United States",
      ),
    },
    customer: {
      name: (input.customer.full_name ?? "").trim() || "Customer",
      addressLines: homeLines(input.customer),
    },
    jobNumber: String(input.jobNumber),
    property: street.length ? street.join(", ") : null,
    description: wholeJob
      ? "Payment in full"
      : input.towardPlan
        ? "Payment plan"
        : described || "Payment",
    method,
    amount,
    contract,
    balanceAfter,
    paidInFull: balanceAfter === 0,
    contact: repName
      ? {
          name: repName,
          phone: (input.rep?.phone ?? "").trim() || null,
          email: (input.rep?.email ?? "").trim() || null,
        }
      : null,
  };
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * "September 10, 2026", from "2026-09-10".
 *
 * Split, never parsed with `new Date`: a date column read as a moment is
 * midnight UTC, which is the previous evening in Alabama, and a receipt dated
 * the day before the money was handed over is a receipt somebody questions.
 */
export function receiptDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-");
  const name = MONTHS[Number(month) - 1];
  if (!year || !name || !day) return iso;
  return `${name} ${Number(day)}, ${year}`;
}

/** Named the way a person filing it would name it. */
export function receiptFilename(number: string): string {
  const safe = number.replace(/[^A-Za-z0-9._-]/g, "-");
  return `receipt-${safe}.pdf`;
}

/**
 * The email the receipt goes out with.
 *
 * Short, because the PDF is the receipt and the letter is only the envelope.
 * Signed by the rep in full, with their own number — the same person the
 * customer has been dealing with, as the proposal is. No licence number,
 * anywhere a customer reads.
 */
/** What the letter thanks them for, from the method the receipt prints. */
function receiptWhat(method: string): string {
  if (method === CUSTOMER_PAYMENT_METHOD_LABEL.cash) return "cash payment";
  if (method === CUSTOMER_PAYMENT_METHOD_LABEL.card) return "card payment";
  if (method === CUSTOMER_PAYMENT_METHOD_LABEL.transfer) return "bank transfer";
  if (method === CUSTOMER_PAYMENT_METHOD_LABEL.financing)
    return "financing payment";
  return "check";
}

export function receiptEmail(
  receipt: ReceiptSnapshot,
  options: {
    firstName?: string | null | undefined;
    /**
     * Whether it goes from the rep's own mailbox. When it cannot — the rep has
     * no Gmail connected — it goes from whoever is sending, and then it names
     * the rep rather than signing as them.
     */
    fromRep?: boolean;
  } = {},
): { subject: string; text: string } {
  const first = (options.firstName ?? "").trim();
  const what = receiptWhat(receipt.method);
  const where = receipt.property ? ` for the work at ${receipt.property}` : "";

  const balance = receipt.paidInFull
    ? "Your balance is now paid in full."
    : receipt.balanceAfter != null
      ? `The remaining balance is ${formatMoney(receipt.balanceAfter)}.`
      : null;

  const contact = receipt.contact;
  const fromRep = options.fromRep ?? true;
  const ask = contact?.phone
    ? fromRep
      ? `If you have any questions, call me on ${contact.phone}.`
      : `If you have any questions, call ${contact.name} on ${contact.phone}.`
    : "If you have any questions, just reply to this email.";

  const signature = [fromRep ? contact?.name : null, receipt.company.name]
    .filter(Boolean)
    .join("\n");

  return {
    subject: `Your receipt from ${receipt.company.name} — ${formatMoney(receipt.amount)}`,
    text:
      `${first ? `Hi ${first},` : "Hello,"}\n\n` +
      `Thank you — we received your ${what} of ${formatMoney(receipt.amount)}${where}. ` +
      `Your receipt ${receipt.number} is attached.\n\n` +
      (balance ? `${balance}\n\n` : "") +
      `${ask}\n\n` +
      `${signature}\n`,
  };
}

/**
 * Whose mailbox a customer's receipt leaves from, and so whose voice it is in.
 *
 * Jonathon, 2026-09-19: "The rep's Gmail, falling back to the approver's
 * mailbox when the rep hasn't connected theirs." (ADR 0140.)
 *
 * The rep first, because the customer has been dealing with the rep and a
 * reply should reach them. Only when the rep has no Gmail connected does it go
 * from whoever approved it — never because some other send went wrong, which
 * would quietly move a letter into a different mailbox and tell nobody.
 *
 * The letter speaks as the owner of the mailbox it leaves from: the rep's
 * mailbox carries the rep's signature and "call me on…"; anybody else's names
 * the rep rather than signing as them (ADR 0140, 2026-09-13).
 *
 * `fromProfileId` is what `gmail-send` is asked for: null is the caller's own
 * mailbox. Sending as the rep when the caller is not the rep needs the admin
 * role, and the Edge Function refuses anybody else rather than downgrading
 * (`resolveEmailSender`).
 */
export interface ReceiptSender {
  /** The profile to send as, or null for the approver's own mailbox. */
  fromProfileId: string | null;
  /** Whether the letter goes out of the rep's mailbox, in the rep's voice. */
  fromRep: boolean;
}

export function receiptSender(input: {
  /** `jobs.rep_id`, if the job has a rep. */
  repProfileId: string | null;
  /** Whoever is approving or sending it. */
  approverProfileId: string | null;
  /** Whether the rep has a Gmail account connected. */
  repHasGmail: boolean;
}): ReceiptSender {
  const rep = input.repProfileId || null;
  const approver = input.approverProfileId || null;
  if (rep && input.repHasGmail) {
    return { fromProfileId: rep === approver ? null : rep, fromRep: true };
  }
  return { fromProfileId: null, fromRep: rep !== null && rep === approver };
}
